import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from "react";
import { cn } from "./cn.js";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "success" | "live" | "warning";
type Size = "xs" | "sm" | "md" | "lg";

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  iconOnly?: boolean;
  leadingIcon?: ReactNode;
};

const variantClasses: Record<Variant, string> = {
  primary:
    "bg-accent text-white hover:bg-accent-strong shadow-[0_1px_0_0_var(--p-hi)_inset]",
  secondary:
    "bg-surface-elevated text-ink border border-border hover:bg-surface-hover hover:border-border-strong",
  ghost: "text-ink-soft hover:bg-surface-elevated hover:text-ink",
  // The family of filled variants (danger/success/live/warning) shares a single
  // font-semibold weight.
  danger: "bg-danger text-white hover:bg-danger-strong font-semibold",
  // A single AA green: confirm/on air (VAR "Ha", Efirga, GOL). The old bg-success
  // text-canvas variant was replaced by this.
  success: "bg-ready-strong text-white hover:brightness-110 font-semibold",
  // On-air red — semantically distinct from danger (a destructive action).
  live: "bg-live text-white hover:brightness-110 font-semibold",
  // The SARIQ card amber (--p-card-amber token).
  warning: "bg-card-amber text-card-amber-ink hover:brightness-110 font-semibold",
};

const sizeClasses: Record<Size, string> = {
  xs: "h-6 px-2 text-[11px] gap-1",
  sm: "h-7 px-2.5 text-[12px] gap-1.5",
  md: "h-8 px-3 text-[13px] gap-2",
  lg: "h-10 px-4 text-[14px] gap-2",
};

const iconOnlySize: Record<Size, string> = {
  xs: "h-6 w-6 px-0",
  sm: "h-7 w-7 px-0",
  md: "h-8 w-8 px-0",
  lg: "h-10 w-10 px-0",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "secondary",
      size = "md",
      iconOnly,
      leadingIcon,
      children,
      ...rest
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={rest.type ?? "button"}
      className={cn(
        "inline-flex items-center justify-center rounded-md font-medium",
        "transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-strong/60",
        variantClasses[variant],
        iconOnly ? iconOnlySize[size] : sizeClasses[size],
        className,
      )}
      {...rest}
    >
      {leadingIcon}
      {children}
    </button>
  ),
);
Button.displayName = "Button";
